import { createFileRoute } from "@tanstack/react-router"; // 1. Added useNavigate
import { OrderTableColumns } from "@/components/order/order-meta";
import { InfoElement } from "@/components/ui/shared/details";
import SimpleTable from "@/components/ui/table";
import { useTranslate } from "@/hooks/use-translate";
import useAppStore from "@/store";
import type { Customer } from "@/store/Customer";
import { ChevronLeft, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { useTableAttributes } from "@/hooks/use-table-attributes";
import { TableAttributesModal } from "@/components/ui/table-attributes-modal";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/(dasboard)/customers/$id")({
  component: () => CustomerPage(),
});

function CustomerPage() {
  const store = useAppStore();

  const { t, formatDate } = useTranslate();
  const customer = store.customer.current as Customer | null;
  const [details, setDetails] = useState<any>([]);
  const tableAttributes = useTableAttributes();
  const tableName = "customers";

  const params = Route.useParams();

  useEffect(() => {
    store.customer.fetchOne(params.id).then((customer) => {
      setDetails([
        { label: t("Name"), value: customer.name },
        { label: t("First Name"), value: customer.first_name },
        { label: t("Last Name"), value: customer.last_name },
        { label: t("Phone"), value: customer.phone_number },
        { label: t("Address"), value: customer.address || "—" },
        { label: t("City"), value: customer.city },
        { label: t("Region"), value: customer.region },
        { label: t("Country"), value: customer.country },
        {
          label: t("Date of Birth"),
          value: customer.date_of_birth
            ? formatDate(customer.date_of_birth)
            : "—",
        },
        { label: t("Created At"), value: formatDate(customer.created_at) },
        { label: t("Updated At"), value: formatDate(customer.updated_at) },
      ]);
    });

    store.order.fetch({
      customer_id: params.id ? params.id : "null",
    });

    tableAttributes.loadAttributes(tableName, [
      "id",
      "customer",
      "status",
      "delivery_status",
      "net_amount",
      "created_at",
      "actions",
    ]);
  }, [params.id, store.auth.session]);

  const columns = OrderTableColumns({
    store,
    onView: (order: any) => {
      store.order.setCurrent(order);
      store.display.show("view_order_block", {
        data: { url: { id: order.id } },
      });
    },
  }).filter((item) =>
    (tableAttributes.attributes[tableName] ?? []).includes(item.name)
  );

  return (
    <section className="max-w-5xl mx-auto">
      <TableAttributesModal
        tableName={tableName}
        columns={OrderTableColumns({ store })}
      />

      <div>
        <button
          type="button"
          onClick={() => window.history.back()} // In SPAs, window.history.back() is the standard for "back"
          className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:underline font-semibold"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="">{t("Back")}</span>
        </button>
      </div>
      <header className="mt-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-xl">{customer?.name}</h2>
        <div className="bg-red-50 text-red-800 rounded-full px-6 py-1">
          {t("Debt")} {" : "}
          <span className="font-semibold">
            {store.shop.formatAmount(customer?.owing || 0)}
          </span>
        </div>
      </header>
      <aside className="mt-4 bg-white rounded-md p-6 border-gray-200 border">
        <ul className="grid grid-cols-3 gap-y-4 px-4 py-2 text-sm">
          {details.map((detail: any) => (
            <InfoElement key={detail.label} detail={detail} />
          ))}
        </ul>
      </aside>

      <header className="mt-6 flex items-center gap-2 items-center justify-between">
        <h2 className="font-semibold">{t("Order lists")}</h2>
        <Button
          onClick={() => {
            store.display.show("table_attributes_modal");
          }}
          color="outline"
          className="border-gray-300 border-1 px-3 py-[0.6rem] bg-white"
        >
          <Settings className="w-4 h-4" />
        </Button>
      </header>

      <aside className="mt-2">
        <SimpleTable items={store.order.pagination.data} columns={columns} />
      </aside>
    </section>
  );
}
